/**
 * Sync Manager for POS Offline Support
 * Handles synchronization of offline sales when connection is restored
 */

import offlineDB from './offlineDB';
import { createPosSale } from '../services/api';

class SyncManager {
  constructor() {
    this.isSyncing = false;
    this.syncInterval = null;
    this.listeners = new Set();
    this.lastSyncTime = null;
    this.syncErrors = [];
  }

  /**
   * Add a listener for sync events
   */
  addListener(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  /**
   * Notify all listeners of sync status change
   */
  notifyListeners(status) {
    this.listeners.forEach(callback => {
      try {
        callback(status);
      } catch (e) {
        console.error('[SyncManager] Listener error:', e);
      }
    });
  }

  /**
   * Initialize sync manager
   */
  async init() {
    // Initialize IndexedDB
    await offlineDB.init();
    
    // Listen for online/offline events
    window.addEventListener('online', () => this.handleOnline());
    window.addEventListener('offline', () => this.handleOffline());

    // Listen for service worker messages
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data.type === 'SYNC_OFFLINE_SALES') {
          this.syncPendingSales();
        }
      });
    }

    // Start periodic sync check (every 30 seconds when online)
    this.startPeriodicSync();

    // Initial sync if online
    if (navigator.onLine) {
      setTimeout(() => this.syncPendingSales(), 2000);
    }

    console.log('[SyncManager] Initialized');
  }

  /**
   * Handle coming online
   */
  async handleOnline() {
    console.log('[SyncManager] Connection restored');
    this.notifyListeners({ type: 'online' });
    
    // Wait a moment for connection to stabilize
    setTimeout(() => this.syncPendingSales(), 1000);
  }

  /**
   * Handle going offline
   */
  handleOffline() {
    console.log('[SyncManager] Connection lost');
    this.notifyListeners({ type: 'offline' });
  }

  /**
   * Start periodic sync check
   */
  startPeriodicSync() {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
    }

    this.syncInterval = setInterval(async () => {
      if (navigator.onLine && !this.isSyncing) {
        const counts = await offlineDB.getOfflineSalesCount();
        if (counts.pending > 0) {
          console.log(`[SyncManager] Periodic sync: ${counts.pending} pending sales`);
          this.syncPendingSales();
        }
      }
    }, 30000); // Every 30 seconds
  }

  /**
   * Stop periodic sync
   */
  stopPeriodicSync() {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
  }

  /**
   * Sync all pending offline sales
   */
  async syncPendingSales(sessionData = null) {
    if (this.isSyncing) {
      console.log('[SyncManager] Sync already in progress');
      return { synced: 0, failed: 0 };
    }

    if (!navigator.onLine) {
      console.log('[SyncManager] Cannot sync - offline');
      return { synced: 0, failed: 0 };
    }

    // Get session data from localStorage if not provided
    if (!sessionData) {
      try {
        const savedSession = localStorage.getItem('pos_session');
        if (savedSession) {
          sessionData = JSON.parse(savedSession);
        }
      } catch (e) {
        console.error('[SyncManager] Error reading session:', e);
      }
    }

    if (!sessionData) {
      console.log('[SyncManager] No session data available for sync');
      return { synced: 0, failed: 0 };
    }

    this.isSyncing = true;
    this.syncErrors = [];
    
    this.notifyListeners({ 
      type: 'sync_start',
      timestamp: new Date().toISOString()
    });

    try {
      const pendingSales = await offlineDB.getPendingOfflineSales();
      
      if (pendingSales.length === 0) {
        console.log('[SyncManager] No pending sales to sync');
        this.isSyncing = false;
        this.notifyListeners({ type: 'sync_complete', synced: 0, failed: 0 });
        return { synced: 0, failed: 0 };
      }

      console.log(`[SyncManager] Syncing ${pendingSales.length} offline sales...`);

      let synced = 0;
      let failed = 0;

      for (const sale of pendingSales) {
        try {
          // Mark as syncing
          await offlineDB.updateOfflineSaleStatus(sale.localId, 'syncing');
          
          this.notifyListeners({ 
            type: 'sync_progress',
            current: synced + failed + 1,
            total: pendingSales.length,
            sale: sale.localId
          });

          // Get device ID
          const deviceId = localStorage.getItem('pos_device_id') || 'UNKNOWN';

          // Attempt to sync
          const response = await createPosSale(
            sessionData.session_id,
            sessionData.terminal.id,
            deviceId,
            {
              branch_id: sale.branch_id,
              terminal_id: sale.terminal_id,
              cashier_id: sale.cashier_id,
              cashier_name: sale.cashier_name,
              items: sale.items,
              payment_method: sale.payment_method,
              payments: sale.payments,
              subtotal: sale.subtotal,
              discount: sale.discount || 0,
              tax: sale.tax,
              total: sale.total,
              change: sale.change || 0,
              offline_sale: true,
              offline_timestamp: sale.createdAt
            }
          );

          // Mark as synced
          await offlineDB.updateOfflineSaleStatus(
            sale.localId, 
            'synced', 
            null, 
            response.data.id
          );
          
          synced++;
          console.log(`[SyncManager] Synced sale ${sale.localId} -> ${response.data.id}`);

        } catch (error) {
          failed++;
          const errorMessage = error.response?.data?.detail || error.message || 'Error desconocido';
          
          this.syncErrors.push({
            localId: sale.localId,
            error: errorMessage,
            timestamp: new Date().toISOString()
          });

          // Mark as error (but allow retry)
          await offlineDB.updateOfflineSaleStatus(sale.localId, 'error', errorMessage);
          
          console.error(`[SyncManager] Failed to sync sale ${sale.localId}:`, errorMessage);

          // If it's a session error, stop syncing
          if (error.response?.status === 401 || error.response?.status === 403) {
            console.log('[SyncManager] Session error - stopping sync');
            break;
          }
        }
      }

      this.lastSyncTime = new Date().toISOString();
      
      // Clean up synced sales older than 24 hours
      await offlineDB.deleteSyncedSales();

      const result = { synced, failed, errors: this.syncErrors };
      
      console.log(`[SyncManager] Sync complete: ${synced} synced, ${failed} failed`);
      this.notifyListeners({ 
        type: 'sync_complete',
        ...result,
        timestamp: this.lastSyncTime
      });

      return result;

    } catch (error) {
      console.error('[SyncManager] Sync error:', error);
      this.notifyListeners({ 
        type: 'sync_error',
        error: error.message
      });
      return { synced: 0, failed: 0, error: error.message };

    } finally {
      this.isSyncing = false;
    }
  }

  /**
   * Register background sync (if supported)
   */
  async registerBackgroundSync() {
    if ('serviceWorker' in navigator && 'sync' in window.registration) {
      try {
        const registration = await navigator.serviceWorker.ready;
        await registration.sync.register('sync-offline-sales');
        console.log('[SyncManager] Background sync registered');
      } catch (error) {
        console.log('[SyncManager] Background sync not available:', error);
      }
    }
  }

  /**
   * Get sync status
   */
  async getStatus() {
    const counts = await offlineDB.getOfflineSalesCount();
    return {
      isOnline: navigator.onLine,
      isSyncing: this.isSyncing,
      lastSyncTime: this.lastSyncTime,
      pendingSales: counts.pending,
      errorSales: counts.error,
      totalOffline: counts.total,
      syncErrors: this.syncErrors
    };
  }

  /**
   * Retry failed sales
   */
  async retryFailedSales() {
    const db = await offlineDB.ensureDB();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['offlineSales'], 'readwrite');
      const store = transaction.objectStore('offlineSales');
      const index = store.index('status');
      const request = index.openCursor(IDBKeyRange.only('error'));

      let updated = 0;
      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          const sale = cursor.value;
          sale.status = 'pending';
          sale.syncError = null;
          cursor.update(sale);
          updated++;
          cursor.continue();
        } else {
          console.log(`[SyncManager] Reset ${updated} failed sales to pending`);
          resolve(updated);
          
          // Trigger sync
          if (updated > 0 && navigator.onLine) {
            setTimeout(() => this.syncPendingSales(), 500);
          }
        }
      };

      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Force sync now
   */
  async forceSyncNow() {
    await this.retryFailedSales();
    return this.syncPendingSales();
  }
}

// Export singleton instance
const syncManager = new SyncManager();
export default syncManager;
